/**
 * Explicit-consent checkbox for health-related (special-category) data,
 * shown wherever a cared-for person's profile is first created (Welcome,
 * SetupDevice's combined claim+name step, AddPersonDialog). The granted
 * consent is recorded on the account doc (healthConsentAt/By/Version);
 * callers gate submission on `checked`.
 *
 * ── LEGAL RATIONALE — do not remove or "simplify" this component ──────
 *
 * This checkbox is the company's lawful basis for holding health data.
 * Calendar entries routinely reveal health information ("GP appointment",
 * medicine names), which is special-category data under UK GDPR Article 9.
 * Our published privacy policy (§4) states we process it on the basis of
 * the caregiver's EXPLICIT CONSENT — and this checkbox is where that
 * consent is captured. Without it we have no Art 9 condition, which is a
 * reportable compliance gap (docs/dpia.md, risk R2, rated High).
 *
 * The legal requirements dictate the shape, so these are load-bearing:
 *  - A real, unticked checkbox: explicit consent needs a clear
 *    AFFIRMATIVE act. Passive "by continuing you agree" wording (like
 *    AuthConsentNotice) does not qualify.
 *  - Separate from terms acceptance: UK GDPR Art 7(2) requires consent
 *    requests to be clearly distinguishable from other matters. Folding
 *    this into the signup terms notice INVALIDATES the consent.
 *  - The label text names what's collected, what we do with it (store,
 *    voice through the button, share with the AI provider), and the
 *    acting-on-behalf confirmation: consent must be specific + informed.
 *    Wording changes need a HEALTH_CONSENT_VERSION bump (schemas) and a
 *    matching privacy-policy §4 review.
 *  - The stored At/By/Version triple is the evidential record (who
 *    agreed, when, to which wording) that answers a future "I never
 *    agreed to this" dispute or an ICO query. firestore.rules keeps it
 *    tamper-evident and requires it at first naming; user_provisioning.py
 *    enforces it on the create_account path.
 *
 * If product changes make this checkbox feel like friction, the options
 * are a different Art 9 condition (there is no comfortable one for a
 * consumer carer product — take legal advice first) or dropping the
 * health-data processing entirely. Silently removing the capture while
 * the privacy policy still claims explicit consent is the one wrong move.
 */

const PRIVACY_URL = 'https://remindmevoice.com/privacy';

export function HealthConsentCheckbox({
  checked,
  onCheckedChange,
  idPrefix,
}: {
  checked: boolean;
  onCheckedChange: (v: boolean) => void;
  /** Keeps input ids unique when two instances could mount in one tree. */
  idPrefix: string;
}) {
  const id = `${idPrefix}-health-consent`;
  return (
    <div className="rounded-lg border border-paper-border bg-white px-3 py-3">
      <label htmlFor={id} className="flex items-start gap-3">
        <input
          id={id}
          type="checkbox"
          checked={checked}
          onChange={(e) => onCheckedChange(e.target.checked)}
          className="mt-0.5 h-5 w-5 shrink-0 accent-accent-600"
        />
        <span className="text-sm text-ink-soft">
          Reminders and calendar entries can include health details — like a
          GP appointment or a medicine. I agree that RemindMeVoice can store
          these and speak them through the button, and share relevant entries
          with our AI provider — when I use the help assistant, and if
          RemindMeVoice needs to look into a problem with the service. I
          confirm I'm setting this up for someone who has agreed, or for whom
          I have the authority to act.
        </span>
      </label>
      <a
        href={PRIVACY_URL}
        target="_blank"
        rel="noreferrer"
        className="mt-2 block pl-8 text-sm text-accent-600 underline hover:no-underline"
      >
        How we look after this information
      </a>
    </div>
  );
}
